import React, { Suspense } from 'react';
import { Box } from '@mui/material';
import { useInView } from '../utils/motion';

/**
 * Defers MOUNTING a section until it approaches the viewport.
 *
 * HomePage already code-splits every below-the-fold section with `React.lazy`
 * and marks them `content-visibility: auto`. Neither actually stops the section
 * from loading: `lazy` defers the JavaScript but the component renders as soon
 * as its chunk lands, and `content-visibility` only skips layout and paint —
 * images and videos inside still download.
 *
 * The cost showed up in the Lighthouse waterfall: on first load the homepage
 * pulled `storefront-poster.jpg` (122 KB), `beauty-treatment-room.jpg` (128 KB)
 * and `beauty-entry.jpg` (91 KB) — none of them visible — plus five separate
 * requests for a `storefront.mp4` that does not exist. All of it competed with
 * the hero image for a throttled 1.6 Mbps connection, which is what pushed
 * Largest Contentful Paint past eight seconds.
 *
 * Gating on IntersectionObserver means those bytes are not requested until the
 * user is within `rootMargin` of needing them.
 *
 * Ported from labeau_landing's `LazySection`, with its
 * `react-intersection-observer` dependency swapped for the native hook in
 * utils/motion.js.
 *
 * `minHeight` MUST approximate the section's real height. The placeholder holds
 * that space open so mounting doesn't shove the page around — the site's
 * Cumulative Layout Shift is currently a perfect 0 and this is the one change
 * here that could spoil it.
 */
const LazySection = ({ children, minHeight = 800, rootMargin = '600px 0px', sx, ...rest }) => {
    const [ref, inView] = useInView(0, true, rootMargin);

    return (
        <Box
            ref={ref}
            /* `sx` is destructured out of the props and merged HERE, with the
               reserved height applied last. Spreading `{...rest}` over an `sx`
               prop instead let the caller's own `sx` replace this one wholesale,
               which silently dropped `minHeight`: every un-mounted section
               collapsed to 0 px, so the whole back half of the homepage stacked
               into a single scroll position. */
            sx={{
                ...sx,
                // Once mounted, release the floor and let real content set the
                // height; before that, hold the space open.
                minHeight: inView ? undefined : `${minHeight}px`,
            }}
            {...rest}
        >
            {inView ? <Suspense fallback={<Box sx={{ minHeight }} />}>{children}</Suspense> : null}
        </Box>
    );
};

export default LazySection;
